import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, MoreVertical, MessageCircle, CreditCard, BookOpen, Share, FolderPlus, Edit, Trash2, Maximize2 } from 'lucide-react';
import './FilesTable.css';

const FilesTable = ({ documents, onChatModal, onFlashcardsModal, onQuizModal, onDeleteDocument }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClass, setSelectedClass] = useState('All Classes');
    const [selectedType, setSelectedType] = useState('All Types');
    const [sortOrder, setSortOrder] = useState('desc');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [activeMenu, setActiveMenu] = useState(null);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const menuRefs = useRef({});
    const buttonRefs = useRef({});

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const toggleFileSelection = (fileId) => {
        setSelectedFiles(prev => 
            prev.includes(fileId) 
                ? prev.filter(id => id !== fileId)
                : [...prev, fileId]
        );
    };

    const toggleSelectAll = () => {
        if (selectedFiles.length === documents.length) {
            setSelectedFiles([]);
        } else {
            setSelectedFiles(documents.map(doc => doc.id));
        }
    };

    const handleSort = () => {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    };

    const toggleMenu = (fileId) => {
        setActiveMenu(activeMenu === fileId ? null : fileId);
    };

    const handleMenuAction = (action, file) => {
        console.log(`${action} clicked for file:`, file.document_name);
        setActiveMenu(null);
        
        switch (action) {
            case 'chat':
                triggerChatModal(file);
                break;
            case 'flashcards':
                triggerFlashcardsModal(file);
                break;
            case 'quiz':
                triggerQuizModal(file);
                break;
            case 'share':
                // Handle share action
                break;
            case 'addToClass':
                // Handle add to class
                break;
            case 'rename':
                // Handle rename
                break;
            case 'delete':
                if (onDeleteDocument) onDeleteDocument(file);
                break;
            default:
                break;
        }
    };

    const getFileIcon = (fileType, documentName) => {
        // Check file type or file extension - prioritize PDF detection
        const isPdf = fileType === 'application/pdf' || 
                     fileType?.includes('pdf') || 
                     documentName?.toLowerCase().endsWith('.pdf');
        
        if (isPdf) {
            return <div className="file-icon pdf-icon">PDF</div>;
        }
        if (fileType?.includes('image')) {
            return <div className="file-icon image-icon">IMG</div>;
        }
        if (fileType?.includes('video')) {
            return <div className="file-icon video-icon">VID</div>;
        }
        if (fileType?.includes('audio')) {
            return <div className="file-icon audio-icon">AUD</div>;
        }
        return <div className="file-icon document-icon">DOC</div>;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
        });
    };

    // Update menu position when menu opens
    useEffect(() => {
        if (activeMenu) {
            // Small delay to ensure button ref is available
            const timer = setTimeout(() => {
                const button = buttonRefs.current[activeMenu];
                if (button) {
                    const rect = button.getBoundingClientRect();
                    const menuWidth = 200;
                    const spacing = 8;

                    // Calculate actual menu height (7 items * ~48px each + padding)
                    const actualMenuHeight = 7 * 48 + 16; // 7 menu items + padding
                    
                    // Position below the button with a small offset to move it lower
                    let top = rect.bottom + spacing + 10; // Added 10px to move it lower
                    let left = rect.right - menuWidth + 60; // Bring it much closer to the button

                    // If not enough space below, place above
                    const spaceAbove = rect.top;
                    const spaceBelow = window.innerHeight - rect.bottom;
                    if (spaceBelow < actualMenuHeight + spacing + 20) {
                        top = rect.top - actualMenuHeight - spacing;
                    }

                    // Clamp horizontally to stay in viewport
                    const minLeft = 8;
                    const maxLeft = window.innerWidth - menuWidth - 8;
                    left = Math.min(Math.max(left, minLeft), maxLeft);

                    // Clamp vertically to stay in viewport
                    const minTop = 8;
                    const maxTop = window.innerHeight - actualMenuHeight - 8;
                    top = Math.min(Math.max(top, minTop), maxTop);

                    setMenuPosition({ top, left });
                }
            }, 0);
            
            return () => clearTimeout(timer);
        }
    }, [activeMenu]);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside any menu container
            const isOutsideAllMenus = Object.values(menuRefs.current).every(ref => 
                !ref || !ref.contains(event.target)
            );
            
            // Also check if click is outside the portal dropdown
            const portalDropdown = document.querySelector('.files-dropdown-menu.portal-dropdown');
            const isOutsidePortal = !portalDropdown || !portalDropdown.contains(event.target);
            
            if (isOutsideAllMenus && isOutsidePortal) {
                setActiveMenu(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activeMenu]);

    // Generate a stable id for a document (backend may use different keys)
    const getDocumentRowId = (doc, fallbackIndex) => {
        return (
            doc.id ||
            doc.document_id ||
            doc.uuid ||
            doc.file_id ||
            doc._id ||
            `row-${fallbackIndex}`
        );
    };

    const triggerChatModal = (document) => {
        setSelectedDocument(document);
        if (onChatModal) onChatModal(document);
        setActiveMenu(null); // Close the dropdown
    }

    const triggerFlashcardsModal = (document) => {
        setSelectedDocument(document);
        if (onFlashcardsModal) onFlashcardsModal(document);
        setActiveMenu(null); // Close the dropdown
    }

    const triggerQuizModal = (document) => {
        setSelectedDocument(document);
        if (onQuizModal) onQuizModal(document);
        setActiveMenu(null); // Close the dropdown
    }
 

    const filteredDocuments = documents.filter(doc => {
        const documentName = doc.document_name || doc.name || doc.filename || '';
        const matchesSearch = documentName.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesClass = selectedClass === 'All Classes' || true; // Add class filtering logic
        const matchesType = selectedType === 'All Types' || true; // Add type filtering logic
        return matchesSearch && matchesClass && matchesType;
    });

    const sortedDocuments = [...filteredDocuments].sort((a, b) => {
        const dateA = new Date(a.created_at || a.upload_date || a.date_uploaded);
        const dateB = new Date(b.created_at || b.upload_date || b.date_uploaded);
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    return (
        <div className="files-table-container">
            <div className="files-table-header">
                <h3>Files</h3>
                <div className="files-controls">
                    <div className="search-container">
                        <Search className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={handleSearch}
                            className="search-input"
                        />
                    </div>
                    <div className="filter-dropdown">
                        <select 
                            value={selectedClass} 
                            onChange={(e) => setSelectedClass(e.target.value)}
                            className="filter-select"
                        >
                            <option value="All Classes">All Classes</option>
                            <option value="Math">Math</option>
                            <option value="Science">Science</option>
                            <option value="History">History</option>
                        </select>
                    </div>
                    <div className="filter-dropdown">
                        <select 
                            value={selectedType} 
                            onChange={(e) => setSelectedType(e.target.value)}
                            className="filter-select"
                        >
                            <option value="All Types">All Types</option>
                            <option value="PDF">PDF</option>
                            <option value="Image">Image</option>
                            <option value="Video">Video</option>
                            <option value="Audio">Audio</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="files-table">
                <div className="table-header">
                    <div className="header-cell checkbox-cell">
                        <input
                            type="checkbox"
                            checked={selectedFiles.length === documents.length && documents.length > 0}
                            onChange={toggleSelectAll}
                            className="header-checkbox"
                        />
                        <span className="header-label">Name</span>
                    </div>
                    <div className="header-cell date-cell">
                        <span className="header-label">Upload date</span>
                        <button 
                            className={`sort-button ${sortOrder}`}
                            onClick={handleSort}
                        >
                            <ChevronDown className="sort-icon" />
                        </button>
                    </div>
                </div>

                <div className="table-body">
                    {sortedDocuments.length > 0 ? (
                        sortedDocuments.map((document, index) => {
                            const rowId = getDocumentRowId(document, index);
                            return (
                            <div key={rowId} className="table-row">
                                <div className="table-cell name-cell">
                                    <input
                                        type="checkbox"
                                        checked={selectedFiles.includes(rowId)}
                                        onChange={() => toggleFileSelection(rowId)}
                                        className="row-checkbox"
                                    />
                                    {getFileIcon(document.file_type, document.document_name)}
                                    <span className="file-name" title={document.document_name}>
                                        {document.document_name || document.name || 'Untitled Document'}
                                    </span>
                                </div>
                                <div className="table-cell date-cell">
                                    <span className="upload-date">
                                        {formatDate(document.created_at || document.upload_date || document.date_uploaded)}
                                    </span>
                                    <div className="row-actions">
                                        <button 
                                            className="files-action-button files-expand-button"
                                            onClick={() => console.log('Expand:', document.document_name)}
                                        >
                                            <Maximize2 className="files-action-icon" />
                                        </button>
                                        <div 
                                            className="menu-container" 
                                            ref={(el) => menuRefs.current[rowId] = el}
                                        >
                                            <button 
                                                ref={(el) => buttonRefs.current[rowId] = el}
                                                className="files-action-button menu-button"
                                                onClick={() => toggleMenu(rowId)}
                                            >
                                                <MoreVertical className="files-action-icon" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            );
                        })
                    ) : (
                        <div className="empty-table">
                            <p>No files found</p>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Portal dropdown for better positioning */}
            {activeMenu && createPortal(
                <div 
                    className="files-dropdown-menu portal-dropdown"
                    style={{
                        position: 'fixed',
                        top: `${menuPosition.top}px`,
                        left: `${menuPosition.left}px`,
                        zIndex: 10000
                    }}
                >
                    {sortedDocuments.map((document, index) => {
                        const rowId = getDocumentRowId(document, index);
                        if (rowId === activeMenu) {
                            return (
                                <React.Fragment key={rowId}>
                                    <button 
                                        className="files-menu-item"
                                        onClick={() => handleMenuAction('chat', document)}
                                    >
                                        <MessageCircle className="files-menu-icon" />
                                        Chat
                                    </button>
                                    <button 
                                        className="files-menu-item"
                                        onClick={() => handleMenuAction('flashcards', document)}
                                    >
                                        <CreditCard className="files-menu-icon" />
                                        Turn into flashcards
                                    </button>
                                    <button 
                                        className="files-menu-item"
                                        onClick={() => handleMenuAction('quiz', document)}
                                    >
                                        <BookOpen className="files-menu-icon" />
                                        Turn into quiz
                                    </button>
                                    {/* <button 
                                        className="files-menu-item"
                                        onClick={() => handleMenuAction('share', document)}
                                    >
                                        <Share className="files-menu-icon" />
                                        Share
                                    </button> */}
                                    {/* <button 
                                        className="files-menu-item"
                                        onClick={() => handleMenuAction('addToClass', document)}
                                    >
                                        <FolderPlus className="files-menu-icon" />
                                        Add to Class
                                    </button> */}
                                    {/* <button 
                                        className="files-menu-item"
                                        onClick={() => handleMenuAction('rename', document)}
                                    >
                                        <Edit className="files-menu-icon" />
                                        Rename
                                    </button> */}
                                    {/* <button 
                                        className="files-menu-item delete-item"
                                        onClick={() => handleMenuAction('delete', document)}
                                    >
                                        <Trash2 className="files-menu-icon" />
                                        Delete
                                    </button> */}
                                </React.Fragment>
                            );
                        }
                        return null;
                    })}
                </div>,
                document.body
            )}
        </div>
    );
};

export default FilesTable;